import { match } from 'ts-pattern'

import { useNotifications } from '@sequencehq/core-components'
import { waitForApprovalOutcome } from 'Automations/simulation'
import { PUBLISH_AUTO_REJECTED_MESSAGE } from 'modules/Cube/communication/external/quotes.api.v1/quotes.api.v1.constants'
import { useLoadQuoteEditor } from 'modules/Cube/communication/external/quotes.api.v1/ports/useLoadQuoteEditor'
import { useSaveQuoteEditor } from 'modules/Cube/communication/external/quotes.api.v1/ports/useSaveQuoteEditor'
import type {
  PublishQuoteData,
  QuoteExecuteScheduleAction
} from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving/useSaveQuote'
import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import {
  CorePortErrors,
  CubePortImplementation,
  CubePortImplementationProp,
  QuoteCubePorts
} from 'modules/Cube/domain/cube.domain.types'
import { useCallback, useState } from 'react'
import { quoteAdapters } from 'modules/Cube/communication/external/quotes.api.v1/adapters/quote.adapters'
import { useNavigate } from 'react-router-dom'
import { extractValidationErrors } from '@sequencehq/api'

type UseQuotePorts = (props: { quoteId?: string }) => CubePortImplementation

export const useQuotePorts: UseQuotePorts = props => {
  const quoteLoader = useLoadQuoteEditor()
  const saveQuoteEditor = useSaveQuoteEditor()
  const { displayNotification, displayPromiseNotification, hideNotification } =
    useNotifications()
  const navigate = useNavigate()

  const [existingData, setExistingData] =
    useState<CubeDomainInterface['queries']['rawData']['data']>()

  const saveQuoteDraft = useCallback(
    async (
      domainOutput: CubeDomainInterface['queries']
    ): Promise<{
      success: boolean
      id?: string
    }> => {
      if (!existingData) {
        throw new Error("Can't save without initialising first")
      }

      const dataToSave = quoteAdapters.out(domainOutput.rawData.data)
      const { products, ...data } = dataToSave

      if (domainOutput.rawData.data.common.id !== 'uninitialized') {
        try {
          const saveResult = await saveQuoteEditor.updateQuote(
            domainOutput.rawData.data.common.id
          )(data, products.new)

          if (saveResult.success) {
            return {
              success: true,
              id: saveResult.quote?.id
            }
          }

          displayNotification('Failed to save quote', { type: 'error' })
          return {
            success: false
          }
        } catch (error) {
          console.error('[saveQuoteDraft] Caught error:', error)
          const validationErrors = extractValidationErrors(error)
          displayNotification(
            validationErrors.length > 0
              ? validationErrors[0]
              : 'Failed to save quote',
            { type: 'error' }
          )
          return {
            success: false
          }
        }
      } else {
        try {
          const createResult = await saveQuoteEditor.createQuote()

          if (createResult.success && createResult.quote?.id) {
            displayNotification('Quote created', { type: 'success' })
            navigate(`/quotes/${createResult.quote.id}`, { replace: true })
          } else {
            displayNotification('Failed to save quote', { type: 'error' })
          }

          return {
            success: createResult.success,
            id: createResult.quote?.id
          }
        } catch {
          displayNotification('Failed to save quote', { type: 'error' })
          return {
            success: false
          }
        }
      }
    },
    [existingData, navigate, displayNotification, saveQuoteEditor]
  )

  const loadCore = useCallback(
    (ctx: CubePortImplementationProp) => async () => {
      const { data: quoteData, error: quoteError } = await quoteLoader(ctx)(
        props.quoteId
      )

      if (quoteError || !quoteData) {
        return {
          data: null,
          error: quoteError || CorePortErrors.Other
        }
      }

      const resolvedData = quoteAdapters.in(quoteData)

      if (resolvedData.data.common.id !== props.quoteId) {
        navigate(`/quotes/${resolvedData.data.common.id}`, { replace: true })
      }

      setExistingData(resolvedData.data)

      const data = await Promise.resolve({
        data: resolvedData.data,
        configuration: {
          ...ctx.configuration,
          currency: {
            default: quoteData.currencies[0],
            enabled: quoteData.currencies
          }
        }
      })

      return {
        data,
        error: null
      }
    },
    [quoteLoader, props.quoteId, navigate]
  )

  const publishQuote = useCallback(
    async (
      data: PublishQuoteData & { awaitApprovalOutcome: boolean }
    ): Promise<{ outcome: 'PUBLISHED' | 'APPROVAL_REQUESTED' } | false> => {
      if (!existingData || !props.quoteId) {
        throw new Error("Can't save without initialising first")
      }

      try {
        const result = await saveQuoteEditor.publishQuote(props.quoteId, {
          recipients: data.recipients,
          sendRecipientsEmail: data.sendRecipientsEmail,
          emailMessage: data.emailMessage,
          makeReadyToSign: data.makeReadyToSign,
          messageForApprover: data.messageForApprover
        })

        if (!result.success) {
          return false
        }

        const outcome = await getApprovalOutcome({
          requested: result.outcome?.outcome ?? 'PUBLISHED',
          // Named for the automation, but it is the id of the run that was
          // started - `PublishQuoteViaApiResult.approvalAutomationId` is an
          // `AutomationExecutionId` on the backend.
          executionId: result.outcome?.approvalAutomationId,
          awaitApprovalOutcome: data.awaitApprovalOutcome
        })

        if (outcome === 'AUTO_REJECTED') {
          displayNotification(PUBLISH_AUTO_REJECTED_MESSAGE, { type: 'error' })
          return { outcome: 'APPROVAL_REQUESTED' }
        }

        displayNotification(
          outcome === 'APPROVAL_REQUESTED'
            ? 'Quote sent for approval'
            : 'Quote published',
          { type: 'success' }
        )

        return { outcome }
      } catch (error) {
        console.error('[publishQuote] Caught error:', error)
        const validationErrors = extractValidationErrors(error)
        displayNotification(
          validationErrors.length > 0
            ? validationErrors[0]
            : 'Failed to publish quote',
          { type: 'error' }
        )
        return false
      }
    },
    [existingData, displayNotification, props.quoteId, saveQuoteEditor]
  )

  const acceptQuote = useCallback(async (): Promise<boolean> => {
    if (!existingData || !props.quoteId) {
      throw new Error("Can't save without initialising first")
    }

    try {
      const result = await displayPromiseNotification(
        saveQuoteEditor.acceptQuote(props.quoteId),
        {
          loading: 'Marking as signed...',
          success: 'Quote marked as signed',
          error: 'Failed to mark quote as signed'
        }
      )
      return result.success
    } catch {
      return false
    }
  }, [existingData, displayPromiseNotification, props.quoteId, saveQuoteEditor])

  const archiveQuote = useCallback(async (): Promise<boolean> => {
    if (!existingData || !props.quoteId) {
      throw new Error("Can't save without initialising first")
    }

    try {
      const result = await displayPromiseNotification(
        saveQuoteEditor.archiveQuote(props.quoteId),
        {
          loading: 'Archiving quote...',
          success: 'Quote archived',
          error: 'Failed to archive quote'
        }
      )
      return result.success
    } catch {
      return false
    }
  }, [existingData, displayPromiseNotification, props.quoteId, saveQuoteEditor])

  const deleteLatestQuoteDraft = useCallback(async () => {
    if (!existingData || !props.quoteId) {
      throw new Error("Can't save without initialising first")
    }

    try {
      const result = await displayPromiseNotification(
        saveQuoteEditor.deleteLatestQuoteDraft(props.quoteId),
        {
          loading: 'Deleting changes...',
          success: 'Changes deleted',
          error: 'Failed to delete changes'
        }
      )
      return result.success
    } catch {
      return false
    }
  }, [existingData, displayPromiseNotification, props.quoteId, saveQuoteEditor])

  const executeQuote = useCallback(
    async (scheduleAction: QuoteExecuteScheduleAction) => {
      if (!existingData || !props.quoteId) {
        throw new Error("Can't save without initialising first")
      }

      const loadingId = displayNotification(
        'Converting quote to billing schedule...',
        { type: 'loading' }
      )

      try {
        const result = await saveQuoteEditor.executeQuote(
          props.quoteId,
          scheduleAction
        )
        hideNotification(loadingId)

        const billingScheduleId = result.quote?.billingScheduleId
        displayNotification('Draft billing schedule created', {
          type: 'success',
          truncate: false,
          'data-testid': 'executeQuote.successToast',
          ...(billingScheduleId && {
            viewLabel: 'View schedule',
            onClickView: () =>
              navigate(`/billing-schedules/${billingScheduleId}`)
          })
        })

        return result.success
      } catch {
        hideNotification(loadingId)
        displayNotification('Failed to convert quote', { type: 'error' })
        return false
      }
    },
    [
      existingData,
      displayNotification,
      hideNotification,
      navigate,
      props.quoteId,
      saveQuoteEditor
    ]
  )

  const duplicateQuote = useCallback(async () => {
    if (!existingData || !props.quoteId) {
      throw new Error("Can't duplicate without initialising first")
    }

    try {
      const result = await displayPromiseNotification(
        saveQuoteEditor.duplicateQuote(props.quoteId),
        {
          loading: 'Duplicating quote...',
          success: 'Quote duplicated',
          error: 'Failed to duplicate quote'
        }
      )

      if (result.success && result.quote) {
        return { newQuoteId: result.quote.id }
      }

      return null
    } catch {
      return null
    }
  }, [existingData, displayPromiseNotification, props.quoteId, saveQuoteEditor])

  const portImplementation: CubePortImplementation = useCallback(
    (ctx): QuoteCubePorts => ({
      in: {
        core: loadCore(ctx),
        customer: () => Promise.resolve(null)
      },
      out: {
        save: saveQuoteDraft,
        archive: archiveQuote,
        quote: {
          publish: publishQuote,
          accept: acceptQuote,
          execute: executeQuote,
          deleteLatestDraft: deleteLatestQuoteDraft,
          duplicate: duplicateQuote
        }
      }
    }),
    [
      loadCore,
      saveQuoteDraft,
      publishQuote,
      acceptQuote,
      executeQuote,
      archiveQuote,
      deleteLatestQuoteDraft,
      duplicateQuote
    ]
  )

  return portImplementation
}

/**
 * What we tell somebody after a publish request that started an approval run.
 *
 * Publishing with an approval workflow should feel like publishing without one,
 * so when they pressed "Publish" we hold on and watch the run rather than
 * telling them it went for approval. Only once it has actually asked a person
 * do we say so.
 *
 *   pressed "Send for review" -> nothing to wait for; they asked for a review
 *   run published it          -> "Quote published", as if there were no workflow
 *   run asked a person        -> "Quote sent for approval", just later
 *   run turned it down        -> say so; nobody is coming to look at it
 *   run took too long         -> the honest fallback: it's with the workflow
 */
const getApprovalOutcome = async ({
  requested,
  executionId,
  awaitApprovalOutcome
}: {
  requested: 'PUBLISHED' | 'APPROVAL_REQUESTED'
  executionId: string | undefined
  awaitApprovalOutcome: boolean
}): Promise<'PUBLISHED' | 'APPROVAL_REQUESTED' | 'AUTO_REJECTED'> => {
  const startedARun = awaitApprovalOutcome && requested === 'APPROVAL_REQUESTED'

  // No run to watch, or nothing they'd want us to wait for. Either way we say
  // what the request itself came back with.
  if (!startedARun || !executionId) return requested

  return (
    match(await waitForApprovalOutcome({ executionId }))
      .with('PUBLISHED', () => 'PUBLISHED' as const)
      .with('REJECTED', () => 'AUTO_REJECTED' as const)
      // Either a person has it now, or the run is taking longer than we'll wait.
      // Both leave the quote with the workflow, which is what we say.
      .with('NEEDS_REVIEW', 'PENDING', () => 'APPROVAL_REQUESTED' as const)
      .exhaustive()
  )
}
